import { CircularLoader } from '@modules/common/components/CircularLoader';
import { FeedIcon } from '@modules/common/components/FeedIcon';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useIsArtefactEditable } from '@modules/common/hooks';
import { Direction } from '@modules/common/types';
import { DASHBOARD_ACTIVE_CELL_ID } from '@modules/dashboard/constants';
import {
  getAddNewAnalysisToDashboardRoute,
  getEmojiFromDashboard,
} from '@modules/dashboard/helpers';
import {
  useAddTextWidget,
  useIsTextWidgetEnabled,
  useUpsertDashboard,
} from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { SAVED_ARTEFACTS_MODAL_ID } from '@modules/leftNavSidebar/components/SavedNavItem/constants';
import {
  BookmarkRounded,
  InsightsRounded,
  TextFieldsRounded,
} from '@mui/icons-material';
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  PopoverOrigin,
  SxProps,
  Theme,
} from '@mui/material';
import { AnalysisUseCase } from 'generated/graphql';
import Lockr from 'lockr';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import scrollIntoView from 'scroll-into-view-if-needed';
import { AddExistingAnalysesToDashboardDialogContent } from '../AddExistingAnalysesToDashboardDialogContent';

interface DashboardAddContentMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  sx?: SxProps<Theme>;
  anchorEl: Element | ((element: Element) => Element) | null;
  rowID?: string;
  cellInsertDirection: Direction;
  shouldShowNewOptions?: boolean;
}

export const DashboardAddContentMenu = ({
  sx,
  rowID,
  isOpen,
  onClose,
  anchorEl,
  anchorOrigin,
  transformOrigin,
  cellInsertDirection,
}: DashboardAddContentMenuProps) => {
  const [selectedAnalysisType, setSelectedAnalysisType] =
    useState<AnalysisUseCase>();

  const isTextWidgetEnabled = useIsTextWidgetEnabled();
  const { openModal } = useMuiModalContext();
  const navigate = useNavigate();

  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const { upsertDashboard, loading: isCreatingDashboard } =
    useUpsertDashboard();
  const addTextWidget = useAddTextWidget({ rowID, cellInsertDirection });

  const scrollContentIntoView = useCallback((cellID: string | undefined) => {
    if (!cellID) {
      return;
    }

    setTimeout(() => {
      const cellElement = document.getElementById(cellID);
      if (cellElement) {
        scrollIntoView(cellElement, {
          scrollMode: 'if-needed',
          block: 'nearest',
          inline: 'nearest',
          behavior: 'smooth',
        });

        if (Lockr.get(DASHBOARD_ACTIVE_CELL_ID)) {
          Lockr.rm(DASHBOARD_ACTIVE_CELL_ID);
        }
      }
    }, 1000);
  }, []);

  useEffect(() => {
    const cellIDToScrollTo: string = Lockr.get(DASHBOARD_ACTIVE_CELL_ID);

    if (cellIDToScrollTo) {
      scrollContentIntoView(cellIDToScrollTo);
    }
  }, [scrollContentIntoView]);

  const isEditable = useIsArtefactEditable({
    editToken: dashboard.editToken,
    status: dashboard.status,
  });

  if (!dashboard || (!isEditable && dashboard.id)) {
    return null;
  }

  const editableDashboard = {
    ...dashboard,
    editToken: dashboard.editToken,
  };

  const addExistingAnalyses = () => {
    openModal({
      contentComponent: () => (
        <AddExistingAnalysesToDashboardDialogContent
          dashboard={editableDashboard}
          onAddAnalysis={(cell) => {
            scrollContentIntoView(cell?.id);
          }}
          rowID={rowID}
          cellInsertDirection={cellInsertDirection}
        />
      ),
      modalProps: {
        id: SAVED_ARTEFACTS_MODAL_ID,
        fullWidth: true,
        maxWidth: 'lg',
        PaperProps: {
          elevation: 3,
          sx: { width: '1000px', height: '600px', borderRadius: 2 },
        },
      },
    });
    onClose();
  };

  const addNewAnalysis = async (useCase: AnalysisUseCase) => {
    setSelectedAnalysisType(useCase);

    // Create a new dashboard if user is trying to add analysis to unsaved dashbaord
    const savedDashboard = dashboard?.id
      ? dashboard
      : await upsertDashboard({ dashboardProps: {} });

    if (!savedDashboard) {
      return;
    }

    const route = getAddNewAnalysisToDashboardRoute({
      dashboard: {
        ...savedDashboard,
        title: savedDashboard.title,
        editToken: savedDashboard.editToken ?? '',
      },
      useCase,
      rowID,
      cellInsertDirection,
    });

    const dashboardName = savedDashboard.title;
    const dashboardEmoji = getEmojiFromDashboard(savedDashboard);

    navigate(route, {
      state: {
        dashboardName,
        dashboardEmoji,
      },
    });

    onClose();
  };

  return (
    <Menu
      sx={sx}
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    >
      <MenuItem onClick={addExistingAnalyses}>
        <ListItemIcon>
          <BookmarkRounded fontSize="small" />
        </ListItemIcon>
        <ListItemText>Saved Items</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => addNewAnalysis(AnalysisUseCase.Default)}
        disabled={isCreatingDashboard}
      >
        <ListItemIcon>
          {isCreatingDashboard &&
          selectedAnalysisType === AnalysisUseCase.Default ? (
            <CircularLoader size={24} />
          ) : (
            <InsightsRounded fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText>New Quantify</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => addNewAnalysis(AnalysisUseCase.Feed)}
        disabled={isCreatingDashboard}
      >
        <ListItemIcon>
          {isCreatingDashboard &&
          selectedAnalysisType === AnalysisUseCase.Feed ? (
            <CircularLoader size={24} />
          ) : (
            <FeedIcon />
          )}
        </ListItemIcon>
        <ListItemText>New Feed</ListItemText>
      </MenuItem>
      {isTextWidgetEnabled ? (
        <MenuItem
          onClick={() => {
            addTextWidget();
            onClose();
          }}
        >
          <ListItemIcon>
            <TextFieldsRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Text</ListItemText>
        </MenuItem>
      ) : null}
    </Menu>
  );
};
